<template>
  <Head>
    <Title>RISER - {{ user.firstname + ' ' + user.lastname }}</Title>
  </Head>

  <div>
    <div class="card tw-overflow-hidden">
      <div
        class="coverpic"
        :style="{ 'background-image': `url(${coverPhoto})` }"
        @click.self="pictureView = 'cover'"
      >
        <div
          v-if="user.has_follow_request_to_me && !user.is_blocked"
          class="tw-absolute tw-right-3 tw-top-3 tw-inline-flex tw-cursor-default tw-items-center tw-rounded-full tw-bg-white tw-px-3 tw-py-1.5"
        >
          <RelationshipUserImage
            :user="user"
            thumb
            class="tw-mr-2 tw-h-10 tw-w-10 tw-rounded-full tw-object-cover"
          />

          <p class="tw-mr-6">
            {{ $t('XY sent a request', [user.firstname]) }}
          </p>

          <InputAppButton
            class="tw-rounded-full tw-bg-black/10 tw-px-4 tw-py-0.5 tw-text-sm"
            @click="showRequestRespondModal = true"
          >
            {{ $t('Answer') }}
          </InputAppButton>
        </div>

        <div v-if="!currentUser" class="tw-absolute tw-bottom-3 tw-left-3 tw-flex tw-gap-2">
          <RelationshipFollowButtonCore
            v-model="user"
            v-slot="{ buttonLabel, buttonIcon, buttonState, loading, disabled }"
          >
            <InputAppButton
              :loading="loading"
              :disabled="disabled"
              class="tw-rounded-full tw-px-6 tw-py-1 tw-text-white"
              :class="buttonState === 'following' ? 'tw-bg-black' : 'tw-bg-riser-blue'"
            >
              <font-awesome-icon :icon="buttonIcon" class="tw-mr-2" />

              {{ buttonLabel }}
            </InputAppButton>
          </RelationshipFollowButtonCore>

          <InputAppButton
            v-if="!user.is_blocked"
            class="tw-flex tw-h-full tw-items-center tw-rounded-full tw-bg-black tw-px-3 tw-py-1 tw-text-white"
            @click="showFollowSecondaryActionDialog = true"
          >
            <font-awesome-icon icon="ellipsis" />
          </InputAppButton>
        </div>
      </div>
      <div class="tw-flex tw-flex-row tw-flex-wrap tw-items-center tw-justify-between">
        <div class="stat-row">
          <div class="stat-block">
            <unit-item :value="user.total_distance">
              <template #value="{ value }">
                <strong>{{ value }}</strong>
              </template>
              <template #unit="{ unit }">
                <span class="tw-ml-1">{{ unit }}</span>
              </template>
            </unit-item>
            <br />
            <span class="shortDescription">{{ $t('Distance') }}</span>
          </div>
          <div class="stat-block">
            <span
              ><strong> <duration-item :duration="user.total_time" format="*h:mm" /> </strong
            ></span>
            <br />
            <span class="shortDescription">{{ $t('Time') }}</span>
          </div>
        </div>
        <div class="userdiv">
          <div class="fullName">
            {{ user.firstname + ' ' + user.lastname }}
          </div>
          <div
            class="userpic"
            :style="{ 'background-image': 'url(' + user.profile_photo_large_url + ')' }"
            @click="pictureView = 'profile'"
          >
            <UserTypeItem
              class="tw-absolute -tw-right-2 -tw-top-2 tw-h-7 tw-w-7"
              :account-type="user.account_type"
            />
          </div>
          <userbadge-item :id="user.id" />
        </div>
        <div class="stat-row">
          <div class="stat-block">
            <unit-item :value="user.total_climb">
              <template #value="{ value }">
                <strong>{{ value }}</strong>
              </template>
              <template #unit="{ unit }">
                <span class="tw-ml-1">{{ unit }}</span>
              </template>
            </unit-item>
            <br />
            <span class="shortDescription">{{ $t('Climb') }}</span>
          </div>
          <div class="stat-block">
            <span
              ><strong>{{ user.trips_count }}</strong></span
            >
            <br />
            <span class="shortDescription">{{ $t('Trips') }}</span>
          </div>
        </div>
      </div>
    </div>
    <image-form-popup
      v-if="pictureView"
      :src="photoSource"
      :editable="currentUser"
      @close="pictureView = ''"
      :onSave="updatePicture"
    />

    <div class="sub-content">
      <NavigationTabLinks :tabs="tabs" />

      <nuxt-page :user-id="user.id" :user="user" @update="update" />
    </div>
  </div>

  <RelationshipRequestRespondDialog
    v-if="showRequestRespondModal"
    :user-id="user.id"
    :user-name="user.firstname"
    @responded="(user: UserProfile) => requestResponded(user)"
    @close="showRequestRespondModal = false"
  />

  <RelationshipFollowSecondaryActionDialog
    v-if="showFollowSecondaryActionDialog"
    :user-id="user.id"
    :user-name="user.firstname"
    :is-following-me="user.is_follower"
    :is-followed-by-me="user.is_followed_by_me"
    @actionExecuted="secondaryActionExecuted"
    @close="showFollowSecondaryActionDialog = false"
  />
</template>

<script lang="ts">
import UserbadgeItem from '~/components/userbadge-item.vue'
import DurationItem from '~/components/duration-item.vue'
import UnitItem from '~/components/unit-item.vue'
import { AxiosError } from 'axios'

import type { Tab } from '~/types/tabs'
import type { UserProfile } from '~/types/user'
import { ANALYTICS_EVENTS } from '~/types/analyticsEvents'

const placeholderUser = {
  firstname: '',
  lastname: '',
  total_distance: 0,
  total_time: 0,
  total_climb: 0,
  trips_count: 0,
  cover_photo_large_url: '/placeholder/cover_photo.png',
  profile_photo_thumb_url: '/placeholder/profile_photo.svg',
  profile_photo_large_url: '/placeholder/profile_photo.svg'
}

export default defineComponent({
  name: 'User',
  components: {
    UserbadgeItem,
    DurationItem,
    UnitItem
  },
  scrollToTop: true,
  async setup(): Promise<{ user: UserProfile }> {
    const authStore = useAuthStore()

    definePageMeta({
      middleware: ['app-check', 'nav-guard']
    })

    const { params } = useRoute()

    const { getUser } = useUserService()
    const { trackEvent } = useAnalyticsTrackingService()

    // User data
    const userId = params && params.id ? parseInt(params.id as string) : authStore.currentUser?.id

    if (!userId) {
      throw createError({ statusCode: 500, fatal: true })
    }

    if (userId == authStore.currentUser?.id) {
      trackEvent(ANALYTICS_EVENTS.profileShow())
    }

    // We do a custom request here instead of getUser because we want to force getting the latest data
    try {
      const profile = await getUser(userId)
      return { user: { ...placeholderUser, ...profile } }
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        if (err.response && err.response.status === 404) {
          throw createError({ statusCode: 404, statusMessage: 'Page Not Found', fatal: true })
        }
      }
      throw err
    }
  },
  data: function (): {
    showAll: boolean
    currentlyVisible: number
    pictureView: string
    showRequestRespondModal: boolean
    showFollowSecondaryActionDialog: boolean
  } {
    return {
      showAll: false,
      currentlyVisible: 10,
      // Picture upload
      pictureView: '',
      showRequestRespondModal: false,
      showFollowSecondaryActionDialog: false
    }
  },
  computed: {
    tabs() {
      const authStore = useAuthStore(this.$pinia)
      const garageStore = useGarageStore(this.$pinia)

      const route = useRoute()

      return [
        {
          link: `/user/${route.params.id}/timeline`,
          fontAwesomeIcon: 'user',
          title: this.$t('Profile')
        },
        {
          fontAwesomeIcon: 'user-group',
          title: this.$t('Followers'),
          tabs: [
            {
              title: this.$t('Followers'),
              link: `/user/${route.params.id}/followers`
            },
            {
              title: this.$t('Following'),
              link: `/user/${route.params.id}/following`
            },
            {
              title: this.$t('Requests'),
              link: `/user/${route.params.id}/follow-requests`,
              hidden: parseInt(route.params.id as string) !== authStore.currentUser?.id
            },
            {
              title: this.$t('Blocked Users'),
              link: `/user/${route.params.id}/blocked`,
              fontAwesomeIcon: 'user-slash',
              hidden: parseInt(route.params.id as string) !== authStore.currentUser?.id
            }
          ]
        },
        {
          link: `/user/${route.params.id}/groups`,
          fontAwesomeIcon: 'users',
          title: this.$t('Groups'),
          hidden: parseInt(route.params.id as string) === authStore.currentUser?.id
        },
        {
          title: this.$t('Roadbook'),
          fontAwesomeIcon: 'map',
          tabs: [
            {
              title: this.$t('Trips'),
              link: `/user/${route.params.id}/roadbook/trips`
            },
            {
              title: this.$t('Sections'),
              link: `/user/${route.params.id}/roadbook/sections`
            },
            {
              title: this.$t('Planned'),
              link: `/user/${route.params.id}/roadbook/planned`,
              hidden: parseInt(route.params.id as string) !== authStore.currentUser?.id
            }
          ]
        },
        {
          title: this.$t('Garage'),
          fontAwesomeIcon: 'motorcycle',
          tabs: [
            {
              title: this.$t('Bikes'),
              link: `/user/${route.params.id}/garage/bikes`,
              count:
                parseInt(route.params.id as string) === authStore.currentUser?.id
                  ? garageStore.bikesThatNeedService
                  : undefined,
              fontAwesomeIcon: 'motorcycle'
            },
            {
              title: this.$t('Helmets'),
              link: `/user/${route.params.id}/garage/helmets`,
              fontAwesomeIcon: 'helmet-safety'
            },
            {
              title: this.$t('Intercoms'),
              link: `/user/${route.params.id}/garage/intercoms`,
              fontAwesomeIcon: 'headset'
            }
          ]
        },
        {
          title: this.$t('More'),
          fontAwesomeIcon: 'bars',
          tabs: [
            {
              title: this.$t('Photos'),
              link: `/user/${route.params.id}/photos`,
              fontAwesomeIcon: 'image'
            },
            {
              link: `/user/${route.params.id}/getaways`,
              title: this.$t('Getaways'),
              fontAwesomeIcon: 'users-viewfinder'
            }
          ]
        }
      ]
    },
    id() {
      return this.user.id
    },
    currentUser() {
      const authStore = useAuthStore(this.$pinia)

      return this.user.id === authStore.currentUser?.id
    },
    fullName() {
      return (
        (this.user.firstname ? this.user.firstname + ' ' : '') +
        (this.user.lastname ? this.user.lastname : '')
      )
    },
    coverPhoto() {
      if (this.user.cover_photo_large_url) {
        return this.user.cover_photo_large_url
      }
      return '/placeholder/cover_photo.png'
    },
    photoSource() {
      if (this.pictureView === 'cover') {
        return this.coverPhoto
      }
      return this.user.profile_photo_large_url
    }
  },
  async updated() {
    const { getUser } = useUserService()
    this.user = await getUser(this.user.id.toString())
  },
  methods: {
    requestResponded(user: UserProfile | null) {
      if (!user) return

      this.showRequestRespondModal = false
      this.user = user
    },

    secondaryActionExecuted(user: UserProfile | null) {
      if (!user) return

      this.showFollowSecondaryActionDialog = false
      this.user = user
    },

    async updatePicture(file: File) {
      const { setCoverPhoto, setProfilePhoto, setUser } = useUserService()
      const data = new FormData()
      data.append('file', file)
      // Turn picture file into url to be used locally (since server can't provide images instantaneously)
      const photoUrl = URL.createObjectURL(file)
      if (this.pictureView === 'cover') {
        await setCoverPhoto(this.user.id.toString(), data)
        this.user.cover_photo_large_url = photoUrl
      } else if (this.pictureView === 'profile') {
        await setProfilePhoto(this.user.id.toString(), data)
        // TODO: This is probably insufficient to ensure that the profile photo is always correct (URL.createObjectURL(file) not persistent through sessions), but a single manual user request will be enough to update it (can't do it here, since we don't know how long the server takes to process it)
        this.user.profile_photo_large_url = photoUrl
        this.user.profile_photo_thumb_url = photoUrl
      }
      setUser(this.user)
      await this.update()
      // Close popup
      this.pictureView = ''
    },
    async update() {
      const authStore = useAuthStore(this.$pinia)
      const { getUser } = useUserService()

      const { params } = useRoute()

      const userId = params && params.id ? parseInt(params.id as string) : authStore.currentUser?.id

      if (!userId) {
        return
      }

      this.user = await getUser(userId)
    }
  }
})
</script>

<style lang="sass" scoped>
@import "@/assets/vars.sass"

.coverpic
  position: relative
  background-position: center
  background-size: cover
  height: 240px
// Make element click-through so coverpic can be clicked easier
.userdiv
  pointer-events: none
// Keep these child elements non-click-through
.userpic
  pointer-events: auto
// Indicate that users can click on pictures
.coverpic, .userpic
  cursor: zoom-in

$statBlocks: 2
.stat-block
  width: calc(100% / $statBlocks)

.stat-row
  width: calc(50% - 20px)

.userdiv // similar to .statBlock
  display: inline-block
  width: calc(100% / $statBlocks)
  margin-top: -50px // Pretty sure this ain't "best practice", but it forces the div to overflow upwards
  > *
    text-align: center
.card
  position: relative

.fullName
  width: 100% * $statBlocks
  box-sizing: border-box
  padding: $padding
  // Center name vertcally
  margin-left: 50%
  transform: translateX(-50%)
  // Styling
  color: #fff
  text-shadow: 0 0 2px #000
  font-size: 2.5em
  font-weight: bold
  line-height: 1.1em

// Prevent name from affecting parent size (which would cause a horizontal scroll bar on marrow browsers
.userdiv
  position: absolute
  left: 50%
  transform: translateX(-50%)
  top: 240px
.fullName
  position: absolute
  bottom: 100%

.userpic
  position: relative
  display: block
  height: 64px
  width: 64px
  border-radius: 50%
  border: 2px solid #fff
  background-position: center
  background-size: cover
  background-color: $gray
  margin: auto

@media(max-width: 900px)
  .stat-row
    width: 100%
</style>
